import React from "react";
import i18n from "i18next";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import {
  AttributeFormat,
  BooleanFormatOptions,
  DateFormatOptions,
  JsonFormatOptions,
  NumberFormatOptions,
  StringArrayFormatOptions,
  StringFormatOptions,
} from "shared/api/api";
import {
  DATE_WITH_TIME_FORMAT,
  MONTH_YEAR,
  MONTH_YEAR_DAY,
  SHORT_DATE_FORMAT,
} from "shared/constants";
import {
  formatDate,
  formatJsonValue,
  formatNumber,
  formatNumberWithOptions,
  formatPercent,
  formatPercentValue,
  formatStringArray,
} from "shared/locale/format";
import { DataType } from "shared/types";
import { splitVisibleItems } from "shared/utils";

import Chip from "features/components/Chip";
import Tooltip from "features/components/Tooltip";
import OverflowText from "features/ui/OverflowText";

import ChipsCell from "./ChipsCell";
import { DECIMALS_DEFAULT, MIN_DECIMALS_DEFAULT } from "./constants";
import MoreItemsButton from "./MoreItemsButton";
import { RawTableCellValue } from "./types";

// SvgIcon hides itself from screen readers by default; a labelled icon is content, so it gets a role and a name.
// The label is not passed as titleAccess: that renders a native <title> tooltip on top of the MUI one.
export const renderBooleanIcon = (value: boolean, label?: string) => {
  const Icon = value ? CheckOutlinedIcon : CloseOutlinedIcon;
  const icon = (
    <Icon
      className="inline"
      sx={{ fontSize: "inherit" }}
      {...(label
        ? { role: "img", "aria-hidden": false, "aria-label": label }
        : undefined)}
    />
  );

  return label ? <Tooltip title={label}>{icon}</Tooltip> : icon;
};

const isChipValue = (value: RawTableCellValue) =>
  typeof value === "number" ||
  typeof value === "boolean" ||
  (typeof value === "string" && value.trim() !== "");

const withOverflow = (text: string, handleOverflow: boolean) =>
  handleOverflow && text.length > 0 ? (
    <OverflowText value={text} breakWords={true} />
  ) : (
    text
  );

export const formatValue = ({
  value,
  dataType,
  decimals = DECIMALS_DEFAULT,
  minDecimals = MIN_DECIMALS_DEFAULT,
  handleOverflow = false,
  format,
  textOnly = false,
}: {
  value: RawTableCellValue;
  dataType: DataType;
  decimals?: number;
  minDecimals?: number;
  handleOverflow?: boolean;
  // The attribute's effective format. The DataType says which member applies, so each branch narrows it itself.
  format?: AttributeFormat;
  // Plain text for callers that coerce the result to a string: chips become their text and a boolean its label.
  textOnly?: boolean;
}) => {
  const chipColors =
    format && "chipColors" in format ? format.chipColors : undefined;

  // The rules match the stored value, so "IN_PROGRESS" matches when underscores show as spaces.
  if (chipColors && !textOnly && isChipValue(value)) {
    const color =
      chipColors.find((rule) => rule.value === value)?.color ?? "default";

    // A grid cell stretches its direct child, so the span takes the full width and the chip keeps the alignment.
    return (
      <span className="min-w-0">
        <Chip
          size="small"
          color={color}
          label={formatValue({ value, dataType, format, textOnly: true })}
        />
      </span>
    );
  }

  if (dataType === DataType.NUMBER) {
    try {
      if (typeof value !== "number") {
        throw new Error();
      }

      return format
        ? formatNumberWithOptions(value, format as NumberFormatOptions)
        : formatNumber(value, decimals, minDecimals);
    } catch {}
  }

  if (dataType === DataType.PERCENTAGE) {
    try {
      return formatPercentValue(Number(value), decimals);
    } catch {}
  }

  if (dataType === DataType.RATIO_PERCENTAGE) {
    try {
      return formatPercent(value as number);
    } catch {}
  }

  if (dataType === DataType.DATE) {
    try {
      return formatDate(
        value as string,
        format ? (format as DateFormatOptions).dateFormat : SHORT_DATE_FORMAT,
        true
      );
    } catch {}
  }

  // Render the calendar date in UTC (not the client's local zone) so the column
  // matches the stored date regardless of viewer timezone.
  if (dataType === DataType.DATE_UTC) {
    try {
      return formatDate(
        value as string,
        SHORT_DATE_FORMAT,
        false,
        false,
        "UTC"
      );
    } catch {}
  }

  if (
    [
      DataType.DATE_WITH_TIME,
      DataType.DATE_WITH_TIME_NO_TZ,
      DataType.DATE_WITH_TIME_UTC,
    ].includes(dataType)
  ) {
    try {
      return formatDate(
        value as string,
        format
          ? (format as DateFormatOptions).dateFormat
          : DATE_WITH_TIME_FORMAT
      );
    } catch {}
  }

  if (dataType === DataType.DATE_WITH_TIME_OR_TIME_AGO_IF_RECENT) {
    try {
      return formatDate(value as string, DATE_WITH_TIME_FORMAT, false, true);
    } catch {}
  }

  if (dataType === DataType.DATE_YEAR_MONTH) {
    try {
      return formatDate(value as string, MONTH_YEAR, true);
    } catch {}
  }

  if (dataType === DataType.DATE_YEAR_MONTH_DAY) {
    try {
      return formatDate(value as string, MONTH_YEAR_DAY, true);
    } catch {}
  }

  if (dataType === DataType.BOOLEAN) {
    const fallbackLabel = i18n.t(value ? "common:yes" : "common:no");

    if (!format) {
      return textOnly ? fallbackLabel : renderBooleanIcon(Boolean(value));
    }

    const { style, trueLabel, falseLabel } = format as BooleanFormatOptions;
    // A cleared admin label falls back to yes / no.
    const label = (value ? trueLabel : falseLabel) || fallbackLabel;

    return style === "text" || textOnly
      ? withOverflow(label, handleOverflow)
      : renderBooleanIcon(Boolean(value), label);
  }

  if (React.isValidElement(value)) {
    return value;
  }

  // Identifiers, VINs and part numbers are names that happen to contain digits, so this path skips the
  // underscore-to-space substitution the default string branch applies to enum-ish values.
  if (dataType === DataType.VERBATIM && value !== null) {
    return withOverflow(String(value), handleOverflow);
  }

  // Objects and arrays have no useful `toString()` — the default string path below turns them into
  // "[object Object]" — so they are serialized instead. The underscore replacement that path does would corrupt
  // JSON keys, so this branch handles the overflow tooltip itself rather than falling through to it. A json column
  // with a format may also arrive as its raw text.
  if (
    dataType === DataType.OBJECT &&
    value !== null &&
    (typeof value === "object" || format)
  ) {
    try {
      const json = format
        ? formatJsonValue(value, (format as JsonFormatOptions).pointer)
        : JSON.stringify(value);

      return json === null ? null : withOverflow(json, handleOverflow);
    } catch {}
  }

  // A string_array column renders as STRING; its array value tells it apart.
  if (dataType === DataType.STRING && format && Array.isArray(value)) {
    if (value.length === 0) {
      return null;
    }

    const { style, separator } = format as StringArrayFormatOptions;
    const items = value.map(String);
    // The full list belongs on the detail page; only a table cell cuts it at maxItems.
    const maxItems = handleOverflow
      ? (format as StringArrayFormatOptions).maxItems
      : null;

    if (style === "chips" && !textOnly) {
      return (
        <ChipsCell
          items={items}
          maxItems={maxItems}
          separator={separator}
          singleLine={handleOverflow}
        />
      );
    }

    const { visible, hidden } = splitVisibleItems(items, maxItems);
    const moreLabel = (hiddenCount: number) =>
      i18n.t("common:chip_list.more", { count: hiddenCount });

    if (hidden.length === 0 || textOnly) {
      return withOverflow(
        formatStringArray(items, { separator, maxItems }, moreLabel),
        handleOverflow
      );
    }

    // Only the visible items truncate, so the "+ N more" button stays in the cell.
    return (
      <span className="inline-flex gap-1 items-center max-w-full">
        {visible.length > 0 && (
          <span className="truncate">
            {formatStringArray(
              visible,
              { separator, maxItems: null },
              moreLabel
            )}
          </span>
        )}
        <MoreItemsButton
          items={items}
          hiddenCount={hidden.length}
          separator={separator}
        />
      </span>
    );
  }

  // by default try to format as string
  try {
    if (value === null) {
      throw new Error();
    }

    const text = value.toString();
    // A format keeps underscores by default, so codes and IDs show as stored.
    const stringValue =
      format && !(format as StringFormatOptions).underscoresAsSpaces
        ? text
        : text.replace(/_/g, " ");

    return withOverflow(stringValue, handleOverflow);
  } catch {}

  // Nothing above could render it: a null cell, or a value whose own `toString` threw.
  return null;
};
